/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * ThemeToggle Component
 * ============================================================================
 *
 * A toggle button for switching between dark and light themes.
 * Supports system preference option via Radix DropdownMenu.
 *
 * ============================================================================
 */

import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, ThemeName } from '@/lib/theme';
import { useTranslation } from 'react-i18next';

interface ThemeToggleProps {
  /** Show dropdown with system option */
  showSystemOption?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
}

export function ThemeToggle({
  showSystemOption = false,
  size = 'md',
  className = ''
}: ThemeToggleProps) {
  const { t } = useTranslation('common');
  const { theme, resolvedTheme, setTheme, toggleTheme, isDark, isSystem } = useTheme();

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  if (showSystemOption) {
    return (
      <ThemeDropdown
        theme={theme}
        setTheme={setTheme}
        size={size}
        className={className}
      />
    );
  }

  return (
    <button
      onClick={toggleTheme}
      className={`
        ${sizeClasses[size]}
        flex items-center justify-center
        rounded-lg
        bg-[var(--color-bg-tertiary)]
        border border-[var(--color-border-default)]
        text-[var(--color-text-secondary)]
        hover:bg-[var(--color-bg-hover)]
        hover:text-[var(--color-text-primary)]
        hover:border-[var(--color-border-strong)]
        transition-all duration-200
        ${className}
      `}
      aria-label={t('themeToggle.switchTo', { theme: isDark ? t('themeToggle.light') : t('themeToggle.dark') })}
      title={t('themeToggle.currently', { theme: isSystem ? t('themeToggle.system') : t(`themeToggle.${resolvedTheme}`) })}
    >
      {isDark ? (
        <Sun size={iconSizes[size]} className="transition-transform duration-200" />
      ) : (
        <Moon size={iconSizes[size]} className="transition-transform duration-200" />
      )}
    </button>
  );
}


/**
 * Theme Dropdown with System Option — Radix DropdownMenu
 */
interface ThemeDropdownProps {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  size: 'sm' | 'md' | 'lg';
  className?: string;
}

function ThemeDropdown({ theme, setTheme, size, className }: ThemeDropdownProps) {
  const { t } = useTranslation('common');

  const sizeClasses = {
    sm: 'h-8 px-2 text-xs',
    md: 'h-10 px-3 text-sm',
    lg: 'h-12 px-4 text-base',
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 20,
  };

  const options: { value: ThemeName; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: t('themeToggle.light'), icon: <Sun size={iconSizes[size]} /> },
    { value: 'dark', label: t('themeToggle.dark'), icon: <Moon size={iconSizes[size]} /> },
    { value: 'system', label: t('themeToggle.system'), icon: <Monitor size={iconSizes[size]} /> },
  ];

  const currentOption = options.find(opt => opt.value === theme) || options[2];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={`
            ${sizeClasses[size]}
            flex items-center gap-2
            rounded-lg
            bg-[var(--color-bg-tertiary)]
            border border-[var(--color-border-default)]
            text-[var(--color-text-secondary)]
            hover:bg-[var(--color-bg-hover)]
            hover:text-[var(--color-text-primary)]
            hover:border-[var(--color-border-strong)]
            transition-all duration-200
            ${className}
          `}
          aria-label={t('themeToggle.selectTheme')}
        >
          {currentOption.icon}
          <span className="hidden sm:inline">{currentOption.label}</span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="theme-dropdown-content"
        >
          {options.map((opt) => (
            <DropdownMenu.Item
              key={opt.value}
              className={`theme-dropdown-item${theme === opt.value ? ' active' : ''}`}
              onSelect={() => setTheme(opt.value)}
            >
              {opt.icon}
              <span>{opt.label}</span>
              {theme === opt.value && <span className="theme-dropdown-check">&#10003;</span>}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>

      <style>{themeDropdownStyles}</style>
    </DropdownMenu.Root>
  );
}

const themeDropdownStyles = `
  .theme-dropdown-content {
    min-width: 140px;
    background: var(--dropdown-bg, var(--color-bg-secondary));
    border: 1px solid var(--dropdown-border, var(--color-border-default));
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    padding: 4px;
    z-index: 9999;
    animation: theme-dropdown-in 0.12s ease-out;
  }

  .theme-dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    color: var(--color-text-primary);
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.1s ease;
    outline: none;
    user-select: none;
  }

  .theme-dropdown-item[data-highlighted] {
    background: var(--dropdown-item-hover, var(--color-bg-hover));
  }

  .theme-dropdown-item.active {
    color: var(--color-brand-primary);
    background: var(--dropdown-item-selected, rgba(139, 0, 0, 0.1));
  }

  .theme-dropdown-item.active[data-highlighted] {
    background: var(--dropdown-item-hover, var(--color-bg-hover));
  }

  .theme-dropdown-check {
    margin-left: auto;
    color: var(--color-brand-primary);
  }

  @keyframes theme-dropdown-in {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
  }
`;

export default ThemeToggle;
